import React from 'react'
import styled from 'styled-components'
import MuiDialog from '@material-ui/core/Dialog'
import MuiDialogContent from '@material-ui/core/DialogContent'
import XIcon from 'src/assets/icons/close-x.svg'
import { breakpoint } from 'src/theme/grid'

/**
 * BaseDialog
 * Niski poziom: tylko wrapper nad @material-ui/core/Dialog + DialogContent.
 */
export const BaseDialog = ({
  open,
  onClose,
  children,
  contentProps,
  ...props
}) => (
  <MuiDialog
    open={Boolean(open)}
    onClose={onClose}
    aria-labelledby='dialog'
    {...props}
  >
    <MuiDialogContent {...contentProps}>{children}</MuiDialogContent>
  </MuiDialog>
)

/**
 * Dialog
 * Wyższy poziom nad BaseDialog z wbudowanym headerem (title) i ikoną zamknięcia.
 * Wymaga props: title (string lub node).
 */
export const Dialog = ({
  title,
  open,
  onClose,
  children,
  hideCloseIcon = false,
  ...props
}) => {
  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='sm'
      {...props}
    >
      <DialogContent>
        {!hideCloseIcon && (
          <ModalCloseIcon onClick={onClose} data-testid='dialog-close' />
        )}
        {title && <DialogHeader>{title}</DialogHeader>}
        {children}
      </DialogContent>
    </BaseDialog>
  )
}

export const DialogContent = styled.div`
  width: 100%;
  min-height: 100%;
  background-color: white;
  position: relative;
  box-sizing: border-box;
  padding: 1.6rem 1.4rem;
  ${breakpoint.m`
    min-height: 260px;
    padding: 0.8rem 0.8rem;
  `}
`
export const DialogHeader = styled.h3`
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  font-size: 1.2rem;
  font-weight: 800;
  line-height: 1.78;
  margin: 0.4rem 0 1.2rem 0;
  width: 95%;
  display: inline-block;
`
export const DialogText = styled.p`
  display: inline-block;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  font-weight: 400;
  line-height: 1.71;
  width: 100%;
  box-sizing: border-box;
  font-size: 0.85rem;
  margin-bottom: 1rem;
`
export const ActionBar = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  background-color: white;
  margin-top: 1.6rem;
  margin-bottom: 0.8rem;
  padding-bottom: 0.8rem;
  gap: 0.9rem 1.2rem;

  ${breakpoint.xs`
    justify-content: flex-end;
  `}
  ${breakpoint.m`
    margin-top: 0.8rem;
    margin-bottom: 0;
  `}
`
const BaseButton = styled.button`
  padding: 0.7rem 1.4rem;
  font-size: 0.875rem;
  font-weight: 600;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    background-color 0.2s,
    color 0.2s,
    box-shadow 0.2s;
  &:disabled {
    opacity: 0.55;
    cursor: default;
    box-shadow: none;
  }
`
export const CancelButton = styled(BaseButton)`
  background-color: ${({ theme }) => theme.colors.wildSand.hex()};
  color: ${({ theme }) => theme.colors.black.hex()};
  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.lightGrey.hex()};
  }
`
export const ConfirmButton = styled(BaseButton)`
  background-color: ${({ theme }) => theme.colors.apple.hex()};
  color: ${({ theme }) => theme.colors.white.hex()};
  box-shadow: 0 2px 10px 0 rgba(71, 170, 66, 0.25);
  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.apple.darken(0.1).hex()};
  }
`
export const ModalCloseIcon = styled(XIcon)`
  cursor: pointer;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  position: absolute;
  top: 22px;
  right: 12px;
  width: 30px;
  height: 30px;
  background-color: transparent;
  border-radius: 50%;
  transition: background-color 0.3s;
  &:hover {
    background-color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.1).hex()};
  }
`
export default Dialog
